import { X } from "lucide-react";

interface Game {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  link: string;
  category: string;
  minBet?: number;
  maxWin?: number;
}

interface GameSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const games: Game[] = [
  {
    id: "degencoinflip",
    title: "Degen Coin Flip",
    description: "Double your tokens with this classic 50/50 game",
    imageUrl: "https://i.imgur.com/tE6QL6M.png",
    link: "https://degencoinflip.com",
    category: "classic",
    minBet: 0.01,
    maxWin: 64,
  },
  {
    id: "crash",
    title: "Crash",
    description: "Place your bet and cash out before the multiplier crashes",
    imageUrl: "https://i.imgur.com/KOT95oO.png",
    link: "https://crash.degencoinflip.com",
    category: "multiplayer",
    minBet: 0.01,
    maxWin: 150,
  },
  {
    id: "degencoindozer",
    title: "Degen Coin Dozer",
    description: "Doze your tokens with this classic game",
    imageUrl: "https://i.imgur.com/54wUbOq.png",
    link: "https://degencoindozer.com",
    category: "classic",
    minBet: 0.01,
    maxWin: 50,
  },
  {
    id: "degenspin",
    title: "Degen Spin",
    description: "Spin to win with our crypto game",
    imageUrl: "https://i.imgur.com/5OAXywp.png",
    link: "https://degenspin.com",
    category: "multiplayer",
    minBet: 0.01,
    maxWin: 100,
  },
];

export function GameSelectionModal({
  isOpen,
  onClose,
}: GameSelectionModalProps) {
  const handleGameClick = (link: string) => {
    window.open(link, "_blank");
  };

  return (
    <div
      className={`fixed inset-0 bg-[#f8f8f8] dark:bg-zinc-900 z-40 transition-all duration-500
        ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      onClick={onClose}
    >
      <div
        className="h-full flex flex-col max-w-[1200px] mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <h2 className="text-2xl font-medium text-zinc-900 dark:text-white">
            Choose Your Game
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/60 dark:hover:bg-zinc-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 pb-6">
          <div className="grid grid-cols-1 gap-6">
            {games.map((game) => (
              <div
                key={game.id}
                onClick={() => handleGameClick(game.link)}
                className="group relative h-[300px] rounded-xl overflow-hidden cursor-pointer
                  transform transition-all duration-500 ease-out
                  hover:shadow-[0_0_40px_rgba(0,0,0,0.1)]
                  dark:hover:shadow-[0_0_40px_rgba(255,255,255,0.1)]
                  hover:-translate-y-1"
              >
                <div className="absolute inset-0">
                  <img
                    src={game.imageUrl}
                    alt={game.title}
                    className="h-full w-full object-cover transition-transform duration-500
                      group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent
                    transition-opacity duration-500 group-hover:opacity-90"
                  >
                    <div
                      className="w-[500px] p-8 h-full flex flex-col justify-center
                      transform transition-all duration-500"
                    >
                      <div className="transform transition-all duration-500 group-hover:translate-x-2">
                        <h2 className="text-3xl font-medium mb-3 text-white">
                          {game.title}
                        </h2>
                        <p
                          className="text-zinc-200 mb-6 text-sm leading-relaxed opacity-90
                          group-hover:opacity-100"
                        >
                          {game.description}
                        </p>
                        <div className="flex gap-8">
                          <div className="transform transition-all duration-500 group-hover:-translate-y-1">
                            <div className="text-2xl font-medium text-white flex items-center gap-1">
                              {game.minBet} ◎
                            </div>
                            <div className="text-sm text-zinc-300">Min Bet</div>
                          </div>
                          <div
                            className="transform transition-all duration-500 group-hover:-translate-y-1
                            delay-75"
                          >
                            <div className="text-2xl font-medium text-white flex items-center gap-1">
                              {game.maxWin} ◎
                            </div>
                            <div className="text-sm text-zinc-300">Max Win</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Play Now Button */}
                  <div
                    className="absolute bottom-8 right-8 transform translate-x-8 opacity-0
                    transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100"
                  >
                    <button
                      className="bg-white text-black px-6 py-3 rounded-full font-medium
                      hover:bg-zinc-100 transition-colors shadow-lg"
                    >
                      Play Now →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
